import { useCallback, useEffect, useRef, useState } from 'react'
import {
  TAccountsTableProps,
  TBankAccount,
  TBankAccountFilters,
  TBusinessAccountsListRequestData,
  TBankAccountsListRequestParams,
  TBankAccountFiltersFunc
} from '../types'
import { columns } from './columns'
import { Table } from 'components/Table'
import { AddBankAccountButton } from '../AddBankAccountButton'
import { DEFAULT_PAGE } from 'components/Table/constants'
import axios, { CancelTokenSource } from 'axios'
import { setLoading } from 'redux/store/common/slice'
import {
  getBankAccountsList,
  getListOfAvailableAccountsToSetAsDefault,
  sendDeposit
} from '../api'
import { useAppDispatch } from 'redux/createStore'
import { EmptyState } from 'features/PaymentDetails/BankAccount/EmptyState'
import { useBankAccountAbility } from '../Providers/BankAccountAbilityProvider'
import { ACTIONS, SUBJECTS } from '../Abilities/constants'
import { NOTIFICATIONS } from 'constants/txt'
import { notification } from 'components/Notification'
import { useBankAccountPopup } from '../Providers/BankAccountPopupProvider'
import { DeleteBankAccount } from '../DeleteBankAccount'
import { VerifyDeposit } from '../VerifyDeposit'
import { formatBankAccounName } from '../helpers'
import { useLocation } from 'react-router-dom'
import { SetAsDefaultReceivableBankAccount } from '../SetAsDefaultReceivableBankAccount'
import { CantProceedWithDefaultReceivableAccount } from '../CantProceedWithDefaultReceivableAccount'
import { ThisAccountIsDefaultReceivableConfirmation } from '../ThisAccountIsDefaultReceivableConfirmation'
import { SetNewDefaultReceivableAccount } from '../SetNewDefaultReceivableAccount'

export const AccountsTable = ({
  businessDetails,
  getBusinessDetails
}: TAccountsTableProps) => {
  const [tableData, setTableData] = useState<TBankAccount[]>([])
  const [filters, setFilters] = useState<TBankAccountFilters>({
    statuses: []
  })
  const [sortParams, setSortParams] = useState<{}>()
  const [firstLoad, setFirstLoad] = useState(true)
  const [totalNumber, setTotalNumber] = useState(0)
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const tableDataTokenRef = useRef<CancelTokenSource>()

  const ability = useBankAccountAbility()

  const {
    deleteBankAccountPopup,
    verifyDepositsPopup,
    setAsDefaultReceivableAccountPopup,
    cantProceedWithDefaultReceivableAccountPopup,
    thisAccountIsDefaultReceivableAccountPopup,
    setAnotherDefaultReceivableAccountPopup
  } = useBankAccountPopup()

  const canSeeBankAccountDetails = ability.can(
    ACTIONS.VIEW,
    SUBJECTS.BANK_ACCOUNT_DETAILS
  )

  const dispatch = useAppDispatch()
  const { pathname } = useLocation()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getTableData = ({
    sortField,
    sortOrder,
    filters,
    page
  }: TBusinessAccountsListRequestData) => {
    dispatch(setLoading(true))
    if (!!tableDataTokenRef.current) {
      tableDataTokenRef.current.cancel()
    }
    const params: TBankAccountsListRequestParams = { ...filters }

    if (sortField) {
      if (sortOrder) {
        params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
      }
      setSortParams({
        sortField,
        sortOrder
      })
    }
    const dataPage = page ? page : pageInfo
    params.limit = dataPage.pageSize
    params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
    tableDataTokenRef.current = axios.CancelToken.source()
    getBankAccountsList(businessDetails.uuid, {
      cancelToken: tableDataTokenRef.current?.token,
      params
    })
      .then((resp) => {
        if (!resp.data?.results) {
          return
        }
        setTableData(resp.data?.results || [])
        setTotalNumber(resp.data.count)
        if (firstLoad) {
          setFirstLoad(false)
        }
      })
      .finally(() => dispatch(setLoading(false)))
  }

  useEffect(() => {
    getTableData({ ...sortParams, filters, page: pageInfo })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDetails.moov_account_id])

  const applyFilters: TBankAccountFiltersFunc = (field, appliedFilters) => {
    const newFilters = { ...filters, [field]: appliedFilters }
    setFilters(newFilters)
    getTableData({ ...sortParams, filters: newFilters })
  }

  useEffect(() => {
    return () => {
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      filters,
      page
    })
    setPageInfo(page)
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      filters,
      page
    })
    setPageInfo(page)
  }

  const onSendDeposit = useCallback(
    async (bankAccountId: string) => {
      try {
        dispatch(setLoading(true))
        await sendDeposit(businessDetails.uuid, bankAccountId)
        getTableData({ ...sortParams, filters, page: pageInfo })
      } catch (error) {
        console.error(error)
        notification.error({
          message: (
            <span>
              {NOTIFICATIONS.DEPOSIT_SEND_ERROR}{' '}
              <span className="text-red">in a few minutes.</span>
            </span>
          )
        })
      } finally {
        dispatch(setLoading(false))
        deleteBankAccountPopup.actions.close()
        deleteBankAccountPopup.actions.close()
      }
    },
    [
      businessDetails.uuid,
      deleteBankAccountPopup.actions,
      dispatch,
      filters,
      getTableData,
      pageInfo,
      sortParams
    ]
  )

  const onDeleteBankAccountPopupOpen = useCallback(
    async (row: TBankAccount) => {
      if (row.is_default) {
        try {
          dispatch(setLoading(true))
          const {
            data: { results: listOfAvailableAccountsToSetAsDefault }
          } = await getListOfAvailableAccountsToSetAsDefault(
            businessDetails.uuid,
            row?.uuid
          )
          if (listOfAvailableAccountsToSetAsDefault.length > 0) {
            thisAccountIsDefaultReceivableAccountPopup.actions.open({
              name: formatBankAccounName(row),
              action: 'delete',
              callback: () => {
                thisAccountIsDefaultReceivableAccountPopup.actions.close()
                setAnotherDefaultReceivableAccountPopup.actions.open({
                  name: formatBankAccounName(row),
                  uuid: row?.uuid,
                  business_account_id: businessDetails.uuid,
                  listOfAvailableAccountsToSetAsDefault,
                  action: 'delete'
                })
              }
            })
          } else {
            cantProceedWithDefaultReceivableAccountPopup.actions.open({
              name: formatBankAccounName(row),
              action: 'delete'
            })
          }
        } finally {
          dispatch(setLoading(false))
        }
      } else {
        deleteBankAccountPopup.actions.open({
          uuid: row.uuid,
          name: formatBankAccounName(row),
          business_account_id: businessDetails.uuid
        })
      }
    },
    [
      businessDetails.uuid,
      cantProceedWithDefaultReceivableAccountPopup.actions,
      deleteBankAccountPopup.actions,
      dispatch,
      setAnotherDefaultReceivableAccountPopup.actions,
      thisAccountIsDefaultReceivableAccountPopup.actions
    ]
  )

  const onVerifyDepositPopupOpen = useCallback(
    (row: TBankAccount) => {
      verifyDepositsPopup.actions.open({
        uuid: row.uuid,
        name: row.nickname,
        account_number: row.account_number,
        business_account_id: businessDetails.uuid
      })
    },
    [businessDetails.uuid, verifyDepositsPopup.actions]
  )

  const onSetAsDefaultReceivableAccountPopupOpen = useCallback(
    (row: TBankAccount) => {
      setAsDefaultReceivableAccountPopup.actions.open({
        uuid: row.uuid,
        name: formatBankAccounName(row),
        business_account_id: businessDetails.uuid
      })
    },
    [businessDetails.uuid, setAsDefaultReceivableAccountPopup.actions]
  )

  const onDeleteBankAccountCallback = useCallback(() => {
    getTableData({ ...sortParams, filters, page: pageInfo })
  }, [getTableData, sortParams, filters, pageInfo])

  const onVerifyDepositCallback = useCallback(() => {
    getTableData({ ...sortParams, filters, page: pageInfo })
  }, [getTableData, sortParams, filters, pageInfo])

  const onSetAsDefaultReceivableAccountCallback = useCallback(() => {
    getTableData({ ...sortParams, filters, page: pageInfo })
  }, [getTableData, sortParams, filters, pageInfo])

  const onSetNewDefaultReceivableAndDeleteCurrentAccountCallback =
    useCallback(() => {
      getTableData({ ...sortParams, filters, page: pageInfo })
    }, [getTableData, sortParams, filters, pageInfo])

  const tableColumns = columns({
    filters,
    applyFilters,
    canSeeBankAccountDetails,
    onSendDeposit,
    onDeleteBankAccount: onDeleteBankAccountPopupOpen,
    onVerifyDeposit: onVerifyDepositPopupOpen,
    onSetAsDefaultReceivableAccount: onSetAsDefaultReceivableAccountPopupOpen,
    redirectRoute: pathname
  })

  const onAddBankAccount = useCallback(() => {
    getBusinessDetails()
    getTableData({ ...sortParams, filters, page: pageInfo })
  }, [filters, getBusinessDetails, getTableData, pageInfo, sortParams])

  return !firstLoad && !tableData.length ? (
    <EmptyState
      onAddBankAccount={onAddBankAccount}
      businessDetails={businessDetails}
    />
  ) : (
    <div className="column gap-4">
      <div className="row justify-end">
        <AddBankAccountButton
          businessDetails={businessDetails}
          onAddBankAccount={onAddBankAccount}
        />
      </div>
      {!firstLoad && (
        <Table<TBankAccount>
          className="banking-accounts-table"
          dataSource={tableData}
          columns={tableColumns}
          onChange={handleTableChange}
          onChangePage={handleChangePageSize}
          pageSize={pageInfo.pageSize}
          pagination={{
            pageSize: pageInfo.pageSize,
            current: pageInfo.pageNumber,
            total: totalNumber
          }}
        />
      )}
      <DeleteBankAccount onSuccess={onDeleteBankAccountCallback} />
      <VerifyDeposit onSuccess={onVerifyDepositCallback} />
      <SetAsDefaultReceivableBankAccount
        onSuccess={onSetAsDefaultReceivableAccountCallback}
      />
      <CantProceedWithDefaultReceivableAccount />
      <ThisAccountIsDefaultReceivableConfirmation />
      <SetNewDefaultReceivableAccount
        onSuccess={onSetNewDefaultReceivableAndDeleteCurrentAccountCallback}
      />
    </div>
  )
}
